import { deleteDB, openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { Card, Link, Op, Pin, Space, SpaceCard, Tag } from "../../../shared/types.ts";

/** Entrada da fila de saída (spec §6.4). A chave (opId, ULID monotônico) dá a ordem de envio. */
export interface OutboxEntry {
  opId: string;
  op: Op;
  createdAt: number;
  attempts: number;
  lastError: string | null;
  /** card.create de imagem: arquivo em pendingBlobs; o blobId entra na op depois do PUT. */
  blobSha?: string;
  /** card.update levada para o card que já existia (dedupe): baseRev fixo, sem rebase, e não serve de base a outras. */
  exact?: true;
}

export interface Schema extends DBSchema {
  cards: { key: string; value: Card; indexes: { bumped: number; created: number; kind: string; tagIds: string; deleted: number } };
  tags: { key: string; value: Tag; indexes: { normalized: string } };
  outbox: { key: string; value: OutboxEntry; indexes: { createdAt: number } };
  /** checkpoint, accountId, schemaVersion, lastSyncAt e prefs (Prefs). */
  meta: { key: string; value: { key: string; value: unknown } };
  pendingBlobs: { key: string; value: { sha256: string; file: Blob } };
  // v2 (fase 4)
  spaces: { key: string; value: Space; indexes: { position: number } };
  spaceCards: { key: [string, string]; value: SpaceCard; indexes: { cardId: string } };
  pins: { key: string; value: Pin; indexes: { position: number } };
  links: { key: string; value: Link; indexes: { sourceId: string; targetId: string } };
}

export type LocalDB = IDBPDatabase<Schema>;

const dbName = (accountId: string) => `faig-${accountId}`;

/**
 * Um banco por conta. v1: cards, tags, fila e meta. v2: coleções, pertenças, Em foco e ligações (os dados e a fila da
 * v1 ficam; o checkpoint volta a 0).
 * `onBlocking`: outra aba quer apagar o banco (logout) ou abrir um esquema mais novo; esta conexão já foi fechada.
 */
export function openLocal(accountId: string, onBlocking?: () => void): Promise<LocalDB> {
  return openDB<Schema>(dbName(accountId), 2, {
    blocking(_current, _next, event) {
      (event.target as IDBDatabase).close(); // senão o deleteDB do logout espera esta aba fechar (§7.10)
      onBlocking?.();
    },
    upgrade(db, old, _new, tx) {
      if (old < 1) {
        const cards = db.createObjectStore("cards", { keyPath: "id" });
        cards.createIndex("bumped", "bumpedAt");
        cards.createIndex("created", "createdAt");
        cards.createIndex("kind", "kind");
        cards.createIndex("tagIds", "tagIds", { multiEntry: true });
        cards.createIndex("deleted", "deletedAt"); // null não entra no índice: só a lixeira
        db.createObjectStore("tags", { keyPath: "id" }).createIndex("normalized", "normalized");
        db.createObjectStore("outbox", { keyPath: "opId" }).createIndex("createdAt", "createdAt");
        db.createObjectStore("meta", { keyPath: "key" });
        db.createObjectStore("pendingBlobs", { keyPath: "sha256" });
        void tx.objectStore("meta").put({ key: "accountId", value: accountId });
      }
      if (old < 2) {
        db.createObjectStore("spaces", { keyPath: "id" }).createIndex("position", "position");
        db.createObjectStore("spaceCards", { keyPath: ["spaceId", "cardId"] }).createIndex("cardId", "cardId");
        db.createObjectStore("pins", { keyPath: "cardId" }).createIndex("position", "position");
        const links = db.createObjectStore("links", { keyPath: "id" });
        links.createIndex("sourceId", "sourceId");
        links.createIndex("targetId", "targetId");
        // O pull da v1 pulou coleções, pertenças, Em foco, ligações e preferências mas avançou o checkpoint: um pull
        // completo traz o que ficou para trás (reaplicar o resto é inofensivo).
        void tx.objectStore("meta").delete("checkpoint");
      }
      void tx.objectStore("meta").put({ key: "schemaVersion", value: 2 });
    },
  });
}

/** Logout (§7.10): apaga a réplica local da conta. Chamar depois de store.stop(); outras abas abertas largam o banco. */
export function clearLocalData(accountId: string): Promise<void> {
  return deleteDB(dbName(accountId));
}

// ---------- Caixa de entrada do compartilhamento (share target, §10.4). Formato em API.md. ----------

/** Um compartilhamento que o service worker recebeu e o app ainda não transformou em card. */
export interface SharedItem {
  id: string;
  createdAt: number;
  title?: string;
  text?: string;
  url?: string;
  files?: File[];
}
interface ShareSchema extends DBSchema {
  shareInbox: { key: string; value: SharedItem };
}
/** Banco à parte (o service worker não sabe a conta). Quem abrir primeiro cria o store. */
export const openShareInbox = () =>
  openDB<ShareSchema>("faig-share", 1, { upgrade: (db) => void db.createObjectStore("shareInbox", { keyPath: "id" }) });
